import { DevTool } from '@hookform/devtools';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useIssueCatchUpMutation } from '../../../api/CatchUpsApi';
import { usePolicy } from '../../../api/PolicyApi';
import { useAuthContext } from '../../../context/auth';
import { usePolicyContext } from '../../../context/policy';
import { CatchUpRequest } from '../../../types/catchUps';
import {
	CurrencyField,
	DateField,
	PolicyholderLookupField,
	PolicyLookupField,
} from '../../fastForm/fields';
import { dateConversion } from '../../fastForm/utils';
import {
	CREATE_CATCH_UP_PAGE__CANCEL,
	CREATE_CATCH_UP_PAGE__SUBMIT,
	CREATE_CATCH_UP_PAGE__TITLE,
} from '../data/constants';
import { catchUpSchema } from '../lib/catchUpSchema';

interface FormValues {
	catchUp: {
		policyholderLocator: string;
		policyLocator: string;
		amount: string;
		dueDate: string;
	};
}

const CreateCatchUpPage = () => {
	const authData = useAuthContext();
	const { authorizationToken: authToken, timezone } = authData.auth;
	const policyData = usePolicyContext();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { data: policyResponse } = usePolicy(
		policyData?.policy?.locator,
		authData?.auth?.authorizationToken,
	);
	const defaultDueDate = dateConversion.dayJsObjectToDateFormString(
		dayjs().add(1, 'day'), // tomorrow
		'day',
	);

	const cashBalanceAmount = policyResponse?.cashBalance?.amount;
	const defaultAmount =
		cashBalanceAmount &&
		Math.abs(parseFloat(cashBalanceAmount)).toFixed(
			cashBalanceAmount.split('.')[1].length,
		);

	const methods = useForm<FormValues>({
		defaultValues: {
			catchUp: {
				policyholderLocator: policyResponse?.policyholderLocator || '',
				policyLocator: policyResponse?.locator || '',
				amount: defaultAmount,
				dueDate: defaultDueDate,
			},
		},
		resolver: yupResolver(catchUpSchema()),
	});

	const { control, handleSubmit } = methods;

	const issue = useIssueCatchUpMutation({
		authToken,
		queryClient,
	});

	const onSubmit: SubmitHandler<FormValues> = async (formValues) => {
		const timestamp = dateConversion.convertDateStringToZonedTimestamp(
			formValues.catchUp.dueDate,
			'day',
			timezone,
		);
		const updateRequest: CatchUpRequest = {
			locator: policyResponse?.cashBalance?.locator,
			request: {
				totalDue: formValues.catchUp.amount,
				dueTimestamp: timestamp,
			},
		};

		const response = await issue.mutateAsync(updateRequest);
		if (response.data.locator) {
			navigate(`..`, { relative: 'path' });
		}
	};
	return (
		<FormProvider {...methods}>
			<Box component='form' noValidate onSubmit={handleSubmit(onSubmit)}>
				<Stack
					spacing={2}
					direction='row'
					justifyContent='space-between'
					alignItems='flex-start'
				>
					<Stack spacing={2} direction='row' alignItems='center' pb={4}>
						<Typography variant='h3'>{CREATE_CATCH_UP_PAGE__TITLE}</Typography>
					</Stack>
					<Stack spacing={2} direction='row'>
						<Button type='button' onClick={() => navigate(-1)}>
							{CREATE_CATCH_UP_PAGE__CANCEL}
						</Button>
						<Button
							variant='contained'
							type='submit'
							disabled={issue.isLoading}
						>
							{CREATE_CATCH_UP_PAGE__SUBMIT}
						</Button>
					</Stack>
				</Stack>
				<Grid container spacing={2}>
					<Grid item xs={6}>
						<PolicyholderLookupField<FormValues>
							policyholderKey={`catchUp.policyholderLocator`}
							order={1}
							name='catchUp.policyholderLocator'
							title='Policyholder'
							control={control}
							disabled
							type='text'
							values={[]}
						/>
					</Grid>
					<Grid item xs={6}>
						<PolicyLookupField<FormValues>
							policyholderKey={`catchUp.policyholderLocator`}
							policyKey={`catchUp.policyLocator`}
							order={2}
							name='catchUp.policyLocator'
							title='Policy'
							control={control}
							disabled
							type='text'
							values={[]}
						/>
					</Grid>
					<Grid item xs={6}>
						<CurrencyField
							order={3}
							name={`catchUp.amount`}
							title='Amount'
							currency={policyResponse.currency}
							disabled={issue.isLoading}
							control={control}
							type='select'
							values={[]}
						/>
					</Grid>
					<Grid item xs={6}>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DateField<FormValues>
								authData={authData}
								control={control}
								showCalendar
								name='catchUp.dueDate'
								title='Due Date'
								disabled={issue.isLoading}
								minimum={defaultDueDate}
								precision='day'
								type='date'
								values={[]}
								order={0}
							/>
						</LocalizationProvider>
					</Grid>
				</Grid>
				<DevTool control={control} />
			</Box>
		</FormProvider>
	);
};

export default CreateCatchUpPage;
